import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Scales, FileText, QrCode, Receipt, ShieldCheck, ArrowsClockwise, Download, PaperPlaneTilt, Microphone, StopCircle, MagicWand, Broadcast } from "@phosphor-icons/react";
import api, { formatApiError, API_BASE } from "../lib/api";
import { formatCurrency } from "../lib/currency";
import DatePicker from "../components/DatePicker";
import FiscalPresetChips from "../components/FiscalPresetChips";
import QuarterPresetChips from "../components/QuarterPresetChips";
import FawtaraOpsPanel from "./oman/FawtaraOpsPanel";
import { StandardReports } from "../components/StandardReports";

const TABS = ["Overview", "VAT Settings", "VAT Ledger", "VAT Return", "E-Invoices", "Payments", "Reports"];

export default function OmanCompliancePage() {
  const [tab, setTab] = useState(new URLSearchParams(window.location.search).get("tab") || "Overview");

  return (
    <div className="p-6 sm:p-8" data-testid="oman-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="font-mono text-[10px] tracking-[0.2em] text-zinc-400">MODULE / OM · OMAN REGULATORY & COMPLIANCE</div>
          <h1 className="font-display text-4xl font-black tracking-tighter mt-1 flex items-center gap-3">
            Oman Compliance.
            <span className="text-xs font-mono px-2 py-1 bg-[#FF3E00] text-white uppercase tracking-widest" data-testid="oman-flag-badge">OMR / OTA / CBO</span>
          </h1>
          <div className="text-sm text-zinc-500 mt-2 max-w-2xl">
            VAT (5%) engine · Fawtara e-invoicing · CBO maker-checker payments. Every AR/AP invoice is VAT-computed and UBL 2.1-exportable.
          </div>
        </div>
      </div>

      <div className="flex border-b border-zinc-950 mb-0 overflow-x-auto">
        {TABS.map((t) => (
          <button
            key={t}
            data-testid={`oman-tab-${t.toLowerCase().replace(/\s/g, "-")}`}
            onClick={() => setTab(t)}
            className={`px-4 py-2.5 text-xs font-semibold tracking-[0.1em] uppercase border-r border-zinc-950 transition-colors whitespace-nowrap ${
              tab === t ? "bg-zinc-950 text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="border-x border-b border-zinc-950 min-h-[60vh]">
        {tab === "Overview" && <Overview />}
        {tab === "VAT Settings" && <VatSettings />}
        {tab === "VAT Ledger" && <VatLedger />}
        {tab === "VAT Return" && <VatReturn />}
        {tab === "E-Invoices" && <EInvoices />}
        {tab === "Payments" && <PaymentsFactory />}
        {tab === "Reports" && <StandardReports module="oman" />}
      </div>
    </div>
  );
}

// ============================================================
// Overview
// ============================================================
function Overview() {
  const items = [
    { icon: Scales, code: "1.2", title: "VAT (OTA) — 5%", desc: "Auto-calc on every invoice · quarterly returns · configurable rate & VRN.", href: "VAT Settings" },
    { icon: Receipt, code: "1.5", title: "Fawtara E-Invoicing", desc: "UBL 2.1 XML generation with TLV QR payload. OTA submission ready (mocked until credentials arrive).", href: "E-Invoices" },
    { icon: ShieldCheck, code: "1.1", title: "CBO Maker-Checker", desc: "Every outbound payment enters a 2-step approval workflow. Fully auditable, dual-authorization.", href: "Payments" },
    { icon: FileText, code: "1.3", title: "IFRS Reporting", desc: "Multi-currency GL structured for IFRS 9 / 15 / 16 (Phase 2).", href: null, coming: true },
    { icon: QrCode, code: "1.4", title: "AML/CFT Monitoring", desc: "Transaction monitoring, KYC/KYB, PEP screening (Phase 2).", href: null, coming: true },
    { icon: PaperPlaneTilt, code: "2.2", title: "H2H Bank Connectivity", desc: "MT940 / camt.053 / SFTP payment file drop (Phase 2, connector-per-bank).", href: null, coming: true },
  ];
  return (
    <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-px bg-zinc-200 border-t border-zinc-950" data-testid="oman-overview">
      {items.map((it) => {
        const Icon = it.icon;
        return (
          <div key={it.title} className="bg-white p-5" data-testid={`oman-overview-${it.code}`}>
            <div className="flex items-start gap-3">
              <Icon size={22} className="mt-0.5 text-[#FF3E00]" weight="duotone" />
              <div className="flex-1 min-w-0">
                <div className="font-mono text-[10px] text-zinc-400 tracking-widest">{it.code}</div>
                <div className="font-display text-lg font-black tracking-tight mt-1">{it.title}</div>
                <div className="text-sm text-zinc-600 mt-1 leading-relaxed">{it.desc}</div>
                {it.coming && (
                  <span className="inline-block mt-2 text-[10px] font-mono px-2 py-0.5 border border-zinc-300 text-zinc-500">PHASE 2</span>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ============================================================
// VAT Settings
// ============================================================
function VatSettings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/oman/vat/settings").then((r) => setS(r.data)).catch(() => {}); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.patch("/oman/vat/settings", s);
      setS(data);
      toast.success("VAT settings updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  if (!s) return <div className="p-8 text-sm text-zinc-500">Loading…</div>;
  return (
    <form onSubmit={save} className="p-6 space-y-4 max-w-2xl" data-testid="vat-settings-form">
      <label className="flex items-center gap-3">
        <input type="checkbox" checked={!!s.vat_registered} onChange={(e) => setS({ ...s, vat_registered: e.target.checked })} data-testid="vat-registered-checkbox" />
        <span className="text-sm">VAT-registered with OTA (Oman Tax Authority)</span>
      </label>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="label-sm text-zinc-500">VAT Registration Number (VRN)</label>
          <input value={s.vrn || ""} onChange={(e) => setS({ ...s, vrn: e.target.value })} placeholder="OM123456789" data-testid="vat-vrn-input" className="w-full border-b border-zinc-300 focus:border-zinc-950 bg-transparent py-2 text-sm outline-none" />
        </div>
        <div>
          <label className="label-sm text-zinc-500">Tax Registration Number (TRN, optional)</label>
          <input value={s.trn || ""} onChange={(e) => setS({ ...s, trn: e.target.value })} data-testid="vat-trn-input" className="w-full border-b border-zinc-300 focus:border-zinc-950 bg-transparent py-2 text-sm outline-none" />
        </div>
        <div>
          <label className="label-sm text-zinc-500">Default rate (%)</label>
          <input type="number" step="0.1" value={s.default_rate} onChange={(e) => setS({ ...s, default_rate: Number(e.target.value) })} data-testid="vat-default-rate-input" className="w-full border-b border-zinc-300 focus:border-zinc-950 bg-transparent py-2 text-sm outline-none tnum" />
        </div>
        <div>
          <label className="label-sm text-zinc-500">Filing frequency</label>
          <select value={s.filing_frequency} onChange={(e) => setS({ ...s, filing_frequency: e.target.value })} data-testid="vat-filing-frequency-select" className="w-full border-b border-zinc-300 bg-transparent py-2 text-sm outline-none">
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly</option>
            <option value="annual">Annual</option>
          </select>
        </div>
      </div>
      <button type="submit" disabled={saving} className="bg-zinc-950 text-white text-xs font-semibold px-5 py-2 hover:bg-[#FF3E00] disabled:opacity-30" data-testid="vat-save-btn">
        {saving ? "SAVING…" : "SAVE"}
      </button>
    </form>
  );
}

// ============================================================
// VAT Ledger
// ============================================================
function VatLedger() {
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const load = async (overrides = {}) => {
    const p = new URLSearchParams();
    const f = overrides.from ?? from;
    const t = overrides.to ?? to;
    if (f) p.set("from", f);
    if (t) p.set("to", t);
    const { data } = await api.get(`/oman/vat/ledger?${p.toString()}`);
    setRows(data.ledger || []);
    setTotals(data.totals || null);
  };
  useEffect(() => { load().catch(() => {}); }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const applyPreset = (nextFrom, nextTo) => {
    setFrom(nextFrom);
    setTo(nextTo);
    load({ from: nextFrom, to: nextTo }).catch(() => {});
  };

  return (
    <div className="p-6" data-testid="vat-ledger-tab">
      <div className="flex items-end gap-3 mb-5 flex-wrap">
        <DatePicker label="From" value={from} onChange={setFrom} placeholder="From date" testId="vat-ledger-from" />
        <DatePicker label="To" value={to} onChange={setTo} placeholder="To date" testId="vat-ledger-to" />
        <button onClick={() => load()} className="text-xs font-mono flex items-center gap-1 border border-zinc-950 px-3 py-1.5 hover:bg-zinc-950 hover:text-white" data-testid="vat-ledger-apply">
          <ArrowsClockwise size={12} /> APPLY
        </button>
        <div className="ml-auto pb-0.5">
          <FiscalPresetChips from={from} to={to} onApply={applyPreset} testIdPrefix="vat-ledger-preset" />
        </div>
      </div>

      {totals && (
        <div className="grid grid-cols-2 md:grid-cols-5 border border-zinc-200 mb-5">
          <Stat label="Output NET (AR)" v={totals.output_net} />
          <Stat label="Output VAT (AR)" v={totals.output_vat} accent />
          <Stat label="Input NET (AP)" v={totals.input_net} />
          <Stat label="Input VAT (AP)" v={totals.input_vat} accent />
          <Stat label="Net VAT Due" v={totals.net_vat_due} highlight />
        </div>
      )}

      <table className="w-full text-sm">
        <thead className="bg-zinc-50 border-b border-zinc-950">
          <tr className="text-xs tracking-wider uppercase">
            <th className="text-left px-3 py-2">Invoice</th>
            <th className="text-left px-3 py-2">Type</th>
            <th className="text-left px-3 py-2">Party</th>
            <th className="text-left px-3 py-2">Date</th>
            <th className="text-left px-3 py-2">Cat</th>
            <th className="text-right px-3 py-2">Net</th>
            <th className="text-right px-3 py-2">VAT</th>
            <th className="text-right px-3 py-2">Gross</th>
          </tr>
        </thead>
        <tbody data-testid="vat-ledger-tbody">
          {rows.length === 0 && (
            <tr><td colSpan={8} className="px-3 py-8 text-center text-zinc-400 text-sm">No VAT-bearing invoices in this range yet.</td></tr>
          )}
          {rows.map((r) => (
            <tr key={r.id} className="border-b border-zinc-100 hover:bg-zinc-50">
              <td className="px-3 py-2 tnum text-xs">{r.invoice_no}</td>
              <td className="px-3 py-2"><span className="text-[10px] font-mono border border-zinc-300 px-1">{r.type}</span></td>
              <td className="px-3 py-2">{r.party}</td>
              <td className="px-3 py-2 text-zinc-500 text-xs">{r.invoice_date}</td>
              <td className="px-3 py-2 text-xs">{r.category}</td>
              <td className="px-3 py-2 text-right tnum">{formatCurrency(r.net, r.currency)}</td>
              <td className="px-3 py-2 text-right tnum text-[#FF3E00] font-semibold">{formatCurrency(r.vat, r.currency)}</td>
              <td className="px-3 py-2 text-right tnum font-semibold">{formatCurrency(r.gross, r.currency)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Stat({ label, v, accent, highlight }) {
  return (
    <div className={`border-r border-zinc-200 last:border-r-0 px-4 py-3 ${highlight ? "bg-zinc-950 text-white" : ""}`}>
      <div className={`label-sm ${highlight ? "text-zinc-400" : "text-zinc-500"}`}>{label}</div>
      <div className={`font-display font-black text-xl tnum mt-1 ${accent && !highlight ? "text-[#FF3E00]" : ""}`}>{formatCurrency(v, "OMR")}</div>
    </div>
  );
}

// ============================================================
// VAT Return (Quarterly)
// ============================================================
function VatReturn() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [quarter, setQuarter] = useState(Math.floor(now.getMonth() / 3) + 1);
  const [ret, setRet] = useState(null);
  const [filed, setFiled] = useState([]);
  const [filing, setFiling] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/oman/vat/return?year=${year}&quarter=${quarter}`);
      setRet(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const loadFiled = async () => {
    try {
      const { data } = await api.get(`/oman/vat/returns/filed`);
      setFiled(data || []);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.warn("filed returns load failed", err);
    }
  };

  useEffect(() => { load().catch(() => {}); }, [year, quarter]);  // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadFiled(); }, []);

  const fileReturn = async () => {
    if (!ret) return;
    setFiling(true);
    try {
      const { data } = await api.post(`/oman/vat/returns/file`, { year, quarter });
      toast.success(`Filed Q${data.quarter} ${data.year} — PDF ${(data.pdf_size / 1024).toFixed(1)} KB`);
      loadFiled();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to file return");
    } finally {
      setFiling(false);
    }
  };

  const downloadFiled = (id, y, q) => {
    const url = `${API_BASE}/api/oman/vat/returns/filed/${id}/pdf`;
    // Use fetch to include cookies then trigger a download of the returned blob.
    fetch(url, { credentials: "include" })
      .then((r) => r.blob())
      .then((blob) => {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `VAT-Q${q}-${y}.pdf`;
        link.click();
        setTimeout(() => URL.revokeObjectURL(link.href), 1000);
      })
      .catch(() => toast.error("Download failed"));
  };

  return (
    <div className="p-6" data-testid="vat-return-tab">
      <VoiceVatFiller
        onApply={(v) => {
          if (v.year) setYear(v.year);
          if (v.quarter) setQuarter(v.quarter);
        }}
      />
      <div className="flex items-end gap-3 mb-6 flex-wrap">
        <div>
          <label className="label-sm text-zinc-500">Year</label>
          <input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="vat-return-year" className="block w-24 border-b border-zinc-300 bg-transparent py-1 text-sm tnum" />
        </div>
        <div>
          <label className="label-sm text-zinc-500">Quarter</label>
          <select value={quarter} onChange={(e) => setQuarter(Number(e.target.value))} data-testid="vat-return-quarter" className="block border-b border-zinc-300 bg-transparent py-1 text-sm">
            {[1, 2, 3, 4].map((q) => <option key={q} value={q}>Q{q}</option>)}
          </select>
        </div>
        <div className="ml-auto pb-0.5">
          <QuarterPresetChips
            year={year}
            quarter={quarter}
            onApply={(y, q) => { setYear(y); setQuarter(q); }}
            testIdPrefix="vat-return-preset"
          />
        </div>
      </div>

      {ret && (
        <div className="border border-zinc-950 max-w-3xl" data-testid="vat-return-card">
          <div className="bg-zinc-950 text-white px-5 py-3 flex items-center justify-between">
            <div>
              <div className="font-mono text-[10px] tracking-widest text-zinc-400">OTA VAT RETURN · Q{ret.period.quarter} {ret.period.year}</div>
              <div className="font-display text-xl font-black tracking-tight">{ret.period.from} → {ret.period.to}</div>
            </div>
            <div className="text-right">
              <div className="font-mono text-[10px] text-zinc-400">VRN</div>
              <div className="font-mono text-sm">{ret.tenant.vrn || "—"}</div>
            </div>
          </div>
          <div className="p-5 space-y-3">
            <Row k="Standard-rated sales (net)" v={ret.totals.output_net} />
            <Row k="Output VAT (5%)" v={ret.totals.output_vat} accent />
            <Row k="Purchases (net)" v={ret.totals.input_net} />
            <Row k="Input VAT reclaim" v={ret.totals.input_vat} accent />
            <div className="border-t border-zinc-950 pt-3">
              <Row k="Net VAT payable to OTA" v={ret.totals.net_vat_due} big />
            </div>
            <div className="flex items-center justify-between pt-3 border-t border-zinc-100">
              <div className="text-xs text-zinc-500">
                {ret.line_count} invoice{ret.line_count === 1 ? "" : "s"} · Generated {new Date(ret.generated_at).toLocaleString()} · by {ret.generated_by}
              </div>
              <button
                onClick={fileReturn}
                disabled={filing || !ret.line_count}
                data-testid="vat-return-file-btn"
                className="bg-[#FF3E00] text-white text-xs font-semibold px-4 py-2 hover:bg-zinc-950 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
                title={ret.line_count ? "Save a permanent PDF filing" : "Nothing to file — no invoices in this period"}
              >
                <Download size={12} /> {filing ? "FILING…" : "FILE RETURN → PDF"}
              </button>
            </div>
          </div>
        </div>
      )}

      <FiledReturnsArchive filed={filed} onDownload={downloadFiled} />
    </div>
  );
}

// ============================================================
// Filed VAT Returns Archive — permanent PDF filings for OTA audits
// ============================================================
function FiledReturnsArchive({ filed, onDownload }) {
  if (!filed?.length) {
    return (
      <div className="max-w-3xl mt-8" data-testid="filed-vat-returns-empty">
        <div className="font-mono text-[10px] tracking-[0.2em] text-zinc-400 uppercase mb-2">Filed Returns Archive</div>
        <div className="border border-dashed border-zinc-300 p-4 text-xs font-mono text-zinc-400">
          NO FILINGS YET — Filed returns will be archived here as permanent PDFs.
        </div>
      </div>
    );
  }
  return (
    <div className="max-w-3xl mt-8" data-testid="filed-vat-returns-list">
      <div className="flex items-center justify-between mb-2">
        <div className="font-mono text-[10px] tracking-[0.2em] text-zinc-400 uppercase">Filed Returns Archive ({filed.length})</div>
        <div className="text-[10px] font-mono text-zinc-400">Permanent PDFs · retain for OTA audit</div>
      </div>
      <div className="border border-zinc-950">
        <table className="w-full text-sm">
          <thead className="bg-zinc-950 text-white">
            <tr className="text-xs tracking-wider uppercase">
              <th className="text-left px-3 py-2">Period</th>
              <th className="text-left px-3 py-2">Filed</th>
              <th className="text-left px-3 py-2">By</th>
              <th className="text-right px-3 py-2">Net VAT</th>
              <th className="text-right px-3 py-2">Lines</th>
              <th className="text-right px-3 py-2">Size</th>
              <th className="w-32" />
            </tr>
          </thead>
          <tbody>
            {filed.map((f) => (
              <tr key={f.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`filed-return-${f.id}`}>
                <td className="px-3 py-2 font-mono text-xs">Q{f.quarter} {f.year}</td>
                <td className="px-3 py-2 text-xs text-zinc-600">{new Date(f.filed_at).toLocaleString()}</td>
                <td className="px-3 py-2 text-xs text-zinc-600 truncate max-w-[220px]" title={f.filed_by}>{f.filed_by}</td>
                <td className="px-3 py-2 text-right tnum font-semibold text-[#FF3E00]">{formatCurrency(f.totals?.net_vat_due, "OMR")}</td>
                <td className="px-3 py-2 text-right tnum text-xs text-zinc-500">{f.line_count}</td>
                <td className="px-3 py-2 text-right tnum text-xs text-zinc-500">{(f.pdf_size / 1024).toFixed(1)} KB</td>
                <td className="px-3 py-2 text-right">
                  <button
                    onClick={() => onDownload(f.id, f.year, f.quarter)}
                    data-testid={`filed-return-download-${f.id}`}
                    className="text-[10px] font-mono text-zinc-700 hover:text-[#FF3E00] flex items-center gap-1 ml-auto"
                  >
                    <Download size={10} /> PDF
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Row({ k, v, accent, big }) {
  return (
    <div className="flex justify-between items-baseline">
      <span className="text-sm text-zinc-600">{k}</span>
      <span className={`tnum ${big ? "font-display text-2xl font-black" : "text-sm font-semibold"} ${accent ? "text-[#FF3E00]" : ""}`}>{formatCurrency(v, "OMR")}</span>
    </div>
  );
}

// ============================================================
// Voice-driven VAT Return filler (uses OpenAI Whisper via backend)
// ============================================================
function VoiceVatFiller({ onApply }) {
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("Microphone not supported in this browser");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
      mediaRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        if (blob.size < 500) { toast.error("Recording too short — try again"); return; }
        setBusy(true);
        const fd = new FormData();
        fd.append("audio", blob, "voice-vat.webm");
        try {
          const r = await api.post("/oman/vat/return/voice", fd, { headers: { "Content-Type": "multipart/form-data" } });
          setResult(r.data);
          toast.success("Transcribed — review below");
        } catch (err) {
          toast.error(formatApiError(err.response?.data?.detail) || "Transcription failed");
        } finally { setBusy(false); }
      };
      mr.start();
      setRecording(true);
    } catch (err) {
      toast.error(`Cannot start recording: ${err.message}`);
    }
  };

  const stop = () => {
    if (mediaRef.current && recording) {
      mediaRef.current.stop();
      setRecording(false);
    }
  };

  const applyToForm = () => {
    if (!result?.extracted) return;
    onApply?.(result.extracted);
    toast.success("Values applied to the VAT return form above");
  };

  return (
    <div className="mb-6 border border-zinc-950 bg-zinc-50" data-testid="voice-vat-panel">
      <div className="bg-zinc-950 text-white px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Microphone size={14} weight="fill" className="text-[#FF3E00]" />
          <span className="font-mono text-[10px] tracking-widest">VOICE-DRIVEN VAT RETURN · OpenAI Whisper</span>
        </div>
        <span className="font-mono text-[10px] text-zinc-300">Say the quarter, year, and amounts.</span>
      </div>
      <div className="p-4 flex flex-wrap items-center gap-3">
        {!recording ? (
          <button
            type="button"
            onClick={start}
            disabled={busy}
            className="flex items-center gap-2 bg-zinc-950 text-white px-4 py-2 text-xs font-semibold hover:bg-[#FF3E00] disabled:opacity-40"
            data-testid="voice-vat-start"
          >
            <Microphone size={14} weight="fill" /> START RECORDING
          </button>
        ) : (
          <button
            type="button"
            onClick={stop}
            className="flex items-center gap-2 bg-[#D0021B] text-white px-4 py-2 text-xs font-semibold pulse-dot"
            data-testid="voice-vat-stop"
          >
            <StopCircle size={14} weight="fill" /> STOP & TRANSCRIBE
          </button>
        )}
        {busy && <span className="text-xs font-mono text-zinc-500 pulse-dot">TRANSCRIBING…</span>}
        <span className="text-xs text-zinc-500 italic">
          Example: <span className="font-mono">"For Q2 2026, net sales were 45,000 rials, output VAT 2,250, purchases 15,000, input VAT 750."</span>
        </span>
      </div>
      {result && (
        <div className="border-t border-zinc-200 p-4 space-y-3 bg-white" data-testid="voice-vat-result">
          <div>
            <div className="font-mono text-[10px] tracking-widest text-zinc-500 mb-1">TRANSCRIPT</div>
            <div className="text-sm italic text-zinc-700 border-l-2 border-zinc-950 pl-3" data-testid="voice-vat-transcript">"{result.transcript}"</div>
          </div>
          <div>
            <div className="font-mono text-[10px] tracking-widest text-zinc-500 mb-1">EXTRACTED VALUES</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
              {[
                ["Quarter", result.extracted.quarter ? `Q${result.extracted.quarter}` : "—", "quarter"],
                ["Year", result.extracted.year || "—", "year"],
                ["Net Sales", result.extracted.net_sales != null ? formatCurrency(result.extracted.net_sales, "OMR") : "—", "net_sales"],
                ["Output VAT", result.extracted.output_vat != null ? formatCurrency(result.extracted.output_vat, "OMR") : "—", "output_vat"],
                ["Purchases", result.extracted.purchases != null ? formatCurrency(result.extracted.purchases, "OMR") : "—", "purchases"],
                ["Input VAT", result.extracted.input_vat != null ? formatCurrency(result.extracted.input_vat, "OMR") : "—", "input_vat"],
                ["Net VAT due", result.extracted.net_vat_due != null ? formatCurrency(result.extracted.net_vat_due, "OMR") : "—", "net_vat_due"],
              ].map(([label, val, key]) => (
                <div key={key} className={`border p-2 ${result.confidence[key] ? "border-emerald-400 bg-emerald-50" : "border-zinc-200 bg-zinc-50"}`} data-testid={`voice-vat-${key}`}>
                  <div className="font-mono text-[9px] tracking-wider text-zinc-500">{label}</div>
                  <div className="font-semibold tnum">{val}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={applyToForm}
              disabled={!result.extracted.quarter && !result.extracted.year}
              className="flex items-center gap-1 bg-zinc-950 text-white px-3 py-1.5 text-xs font-semibold hover:bg-[#FF3E00] disabled:opacity-40"
              data-testid="voice-vat-apply"
            >
              <MagicWand size={12} weight="fill" /> APPLY TO FORM
            </button>
            <button
              type="button"
              onClick={() => setResult(null)}
              className="text-xs font-mono text-zinc-500 hover:text-zinc-950"
              data-testid="voice-vat-discard"
            >Discard</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// E-Invoices (Fawtara)
// ============================================================
function EInvoices() {
  const [invoices, setInvoices] = useState([]);
  const [busy, setBusy] = useState(null);
  const [selected, setSelected] = useState(null);
  const arInvoices = useMemo(() => invoices.filter((i) => i.type === "AR"), [invoices]);

  const load = async () => {
    const { data } = await api.get("/finance/invoices");
    setInvoices(data);
  };
  useEffect(() => { load().catch(() => {}); }, []);

  const generate = async (id) => {
    setBusy(id);
    try {
      await api.post(`/oman/e-invoice/${id}/generate`);
      toast.success("E-invoice UBL XML + QR generated");
      await load();
      setSelected(id);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const submit = async (id) => {
    setBusy(id);
    try {
      const { data } = await api.post(`/oman/e-invoice/${id}/submit`);
      if (data.queued_for_retry) {
        toast.warning(`Submission queued for retry · ${data.error?.slice(0, 80) || "transient error"}`);
      } else {
        toast.success(`Submitted (${data.mode}) · ref ${data.submission_ref}`);
      }
      await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const sandboxPing = async (id) => {
    setBusy(id);
    try {
      const { data } = await api.post(`/oman/e-invoice/${id}/sandbox-ping`);
      if (data.accepted) {
        toast.success(`Sandbox accepted the schema (${data.sandbox_mode}) · ${data.ack?.peppol_message_id || "OK"}`);
      } else {
        const failed = (data.ack?.checks || []).filter((c) => !c.passed).map((c) => c.rule).join(", ");
        toast.warning(`Sandbox rejected · ${failed || "check the ping detail"}`);
      }
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const submitAll = async () => {
    const pending = arInvoices.filter((i) => i.e_invoice_status === "generated").length;
    if (!pending) { toast.info("Nothing to file — every generated e-invoice is already submitted"); return; }
    if (!window.confirm(`File ${pending} generated e-invoice${pending === 1 ? "" : "s"} to Fawtara now?`)) return;
    setBusy("all");
    try {
      const { data } = await api.post("/oman/e-invoice/submit-all", {});
      toast.success(`Bulk filing done · ${data.submitted} submitted · ${data.queued} queued · ${data.rejected} rejected`);
      await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const pendingCount = arInvoices.filter((i) => i.e_invoice_status === "generated").length;

  return (
    <div className="p-6" data-testid="e-invoices-tab">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="text-sm text-zinc-600 max-w-3xl">
          Generate a UBL 2.1 XML + TLV QR payload for every AR invoice. Ping the OTA Peppol sandbox to prove the schema is accepted before real filings; failed live submissions are auto-retried with exponential backoff.
        </div>
        <button onClick={submitAll} disabled={busy === "all" || pendingCount === 0} className="text-[10px] font-mono px-3 py-2 bg-[#FF3E00] text-white disabled:opacity-30 whitespace-nowrap" data-testid="einv-submit-all-btn">
          SUBMIT ALL{pendingCount ? ` (${pendingCount})` : ""}
        </button>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-zinc-50 border-b border-zinc-950">
          <tr className="text-xs tracking-wider uppercase">
            <th className="text-left px-3 py-2">Invoice</th>
            <th className="text-left px-3 py-2">Party</th>
            <th className="text-right px-3 py-2">Gross</th>
            <th className="text-left px-3 py-2">E-Invoice</th>
            <th className="text-left px-3 py-2">Actions</th>
          </tr>
        </thead>
        <tbody data-testid="e-invoices-tbody">
          {arInvoices.length === 0 && (
            <tr><td colSpan={5} className="px-3 py-8 text-center text-zinc-400 text-sm">No AR invoices yet. Create one on the Finance page.</td></tr>
          )}
          {arInvoices.map((i) => (
            <tr key={i.id} className="border-b border-zinc-100 hover:bg-zinc-50">
              <td className="px-3 py-2 tnum text-xs">{i.invoice_no}</td>
              <td className="px-3 py-2">{i.party}</td>
              <td className="px-3 py-2 text-right tnum">{formatCurrency(i.amount, i.currency || "OMR")}</td>
              <td className="px-3 py-2">
                {i.e_invoice_status ? (
                  <span className={`text-[10px] font-mono px-1.5 py-0.5 ${i.e_invoice_status === "submitted" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{i.e_invoice_status.toUpperCase()}</span>
                ) : <span className="text-zinc-400 text-xs">—</span>}
              </td>
              <td className="px-3 py-2 flex gap-2 flex-wrap">
                <button onClick={() => generate(i.id)} disabled={busy === i.id} className="text-[10px] font-mono px-2 py-1 border border-zinc-950 hover:bg-zinc-950 hover:text-white disabled:opacity-30" data-testid={`einv-generate-${i.id}`}>
                  {i.e_invoice_status ? "REGEN" : "GENERATE"}
                </button>
                {i.e_invoice_status && (
                  <>
                    <a href={`${API_BASE}/api/oman/e-invoice/${i.id}/xml`} target="_blank" rel="noreferrer" className="text-[10px] font-mono px-2 py-1 border border-zinc-300 hover:border-zinc-950 flex items-center gap-1" data-testid={`einv-xml-${i.id}`}>
                      <Download size={10} /> XML
                    </a>
                    <button onClick={() => setSelected(selected === i.id ? null : i.id)} className="text-[10px] font-mono px-2 py-1 border border-zinc-300 hover:border-zinc-950 flex items-center gap-1" data-testid={`einv-qr-${i.id}`}>
                      <QrCode size={10} /> QR
                    </button>
                    <button onClick={() => sandboxPing(i.id)} disabled={busy === i.id} className="text-[10px] font-mono px-2 py-1 border border-zinc-300 hover:border-zinc-950 flex items-center gap-1 disabled:opacity-30" data-testid={`einv-sandbox-${i.id}`}>
                      <Broadcast size={10} /> SANDBOX PING
                    </button>
                    {i.e_invoice_status !== "submitted" && (
                      <button onClick={() => submit(i.id)} disabled={busy === i.id} className="text-[10px] font-mono px-2 py-1 bg-[#FF3E00] text-white disabled:opacity-30" data-testid={`einv-submit-${i.id}`}>
                        SUBMIT
                      </button>
                    )}
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {selected && (
        <div className="mt-6 border border-zinc-950 p-5 max-w-md" data-testid="einv-qr-panel">
          <div className="font-mono text-[10px] tracking-widest text-zinc-500 mb-3">FAWTARA QR PREVIEW</div>
          <img src={`${API_BASE}/api/oman/e-invoice/${selected}/qr`} alt="QR code" className="w-64 h-64 border border-zinc-200" />
          <div className="text-[10px] font-mono text-zinc-500 mt-3">TLV payload — decode with any Fawtara-compatible reader.</div>
        </div>
      )}

      <FawtaraOpsPanel />
    </div>
  );
}

// ============================================================
// Payments Factory + Maker-Checker
// ============================================================
function PaymentsFactory() {
  const [payments, setPayments] = useState([]);
  const [form, setForm] = useState({ payment_type: "supplier", beneficiary_name: "", beneficiary_account: "", amount: "", currency: "OMR", memo: "" });
  const [submitting, setSubmitting] = useState(false);
  const [needsTemplate, setNeedsTemplate] = useState(false);
  const [me, setMe] = useState(null);

  const load = async () => {
    const [pRes, meRes, tRes] = await Promise.all([
      api.get("/oman/payments"),
      api.get("/auth/me"),
      api.get("/workflows/templates").catch(() => ({ data: [] })),
    ]);
    setPayments(pRes.data);
    setMe(meRes.data);
    setNeedsTemplate(!(tRes.data || []).some((t) => t.resource_type === "payment" && t.active));
  };
  useEffect(() => { load().catch(() => {}); }, []);

  const create = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/oman/payments", { ...form, amount: Number(form.amount) });
      toast.success("Payment initiated — pending approval");
      setForm({ ...form, beneficiary_name: "", beneficiary_account: "", amount: "", memo: "" });
      await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  const seedTemplate = async () => {
    if (!me) return;
    try {
      await api.post("/oman/payments/maker-checker/seed-template", { approver_ids: [me.id], threshold: 0 });
      toast.success("Default CBO maker-checker template created");
      await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const release = async (pid) => {
    try {
      await api.post(`/oman/payments/${pid}/release`, { comment: "Released via UI" });
      toast.success("Payment executed");
      await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="p-6" data-testid="payments-tab">
      {needsTemplate && (
        <div className="border border-[#FF3E00] bg-orange-50 p-4 mb-5 flex items-start gap-3" data-testid="mc-seed-banner">
          <ShieldCheck size={22} className="text-[#FF3E00] mt-0.5" weight="duotone" />
          <div className="flex-1">
            <div className="font-semibold text-sm">Enable CBO Maker-Checker</div>
            <div className="text-xs text-zinc-600 mt-1">Every payment should require dual authorization. Seed the default 2-step template to activate governance.</div>
          </div>
          <button onClick={seedTemplate} className="text-[10px] font-mono px-3 py-1.5 bg-zinc-950 text-white hover:bg-[#FF3E00]" data-testid="mc-seed-btn">SEED TEMPLATE</button>
        </div>
      )}

      <form onSubmit={create} className="grid grid-cols-1 md:grid-cols-6 gap-px bg-zinc-200 border border-zinc-950 mb-5" data-testid="payment-form">
        <select value={form.payment_type} onChange={(e) => setForm({ ...form, payment_type: e.target.value })} data-testid="pay-type" className="bg-white px-3 py-3 text-sm outline-none">
          <option value="supplier">Supplier</option>
          <option value="salary">Salary/WPS</option>
          <option value="tax">Tax/OTA</option>
          <option value="domestic">Domestic</option>
          <option value="international">International</option>
        </select>
        <input value={form.beneficiary_name} onChange={(e) => setForm({ ...form, beneficiary_name: e.target.value })} required placeholder="Beneficiary name" data-testid="pay-name" className="bg-white px-3 py-3 text-sm outline-none md:col-span-2" />
        <input value={form.beneficiary_account} onChange={(e) => setForm({ ...form, beneficiary_account: e.target.value })} placeholder="IBAN / account" data-testid="pay-iban" className="bg-white px-3 py-3 text-sm outline-none" />
        <input type="number" step="0.001" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required placeholder="Amount" data-testid="pay-amount" className="bg-white px-3 py-3 text-sm outline-none tnum" />
        <button type="submit" disabled={submitting} className="bg-zinc-950 text-white text-xs font-semibold px-4 hover:bg-[#FF3E00] disabled:opacity-30" data-testid="pay-submit">
          INITIATE
        </button>
      </form>

      <table className="w-full text-sm">
        <thead className="bg-zinc-50 border-b border-zinc-950">
          <tr className="text-xs tracking-wider uppercase">
            <th className="text-left px-3 py-2">Ref</th>
            <th className="text-left px-3 py-2">Type</th>
            <th className="text-left px-3 py-2">Beneficiary</th>
            <th className="text-right px-3 py-2">Amount</th>
            <th className="text-left px-3 py-2">Approval</th>
            <th className="text-left px-3 py-2">Status</th>
            <th className="text-left px-3 py-2" />
          </tr>
        </thead>
        <tbody data-testid="payments-tbody">
          {payments.length === 0 && (
            <tr><td colSpan={7} className="px-3 py-8 text-center text-zinc-400 text-sm">No payments yet.</td></tr>
          )}
          {payments.map((p) => (
            <tr key={p.id} className="border-b border-zinc-100 hover:bg-zinc-50">
              <td className="px-3 py-2 tnum text-xs">{p.payment_ref}</td>
              <td className="px-3 py-2 text-xs">{p.payment_type}</td>
              <td className="px-3 py-2">{p.beneficiary_name}<div className="text-[10px] text-zinc-400 font-mono">{p.beneficiary_account}</div></td>
              <td className="px-3 py-2 text-right tnum font-semibold">{formatCurrency(p.amount, p.currency)}</td>
              <td className="px-3 py-2"><span className={`text-[10px] px-1.5 py-0.5 ${p.approval_status === "approved" ? "bg-emerald-50 text-emerald-700" : p.approval_status === "rejected" ? "bg-red-50 text-[#D0021B]" : "bg-amber-50 text-amber-700"}`}>{(p.approval_status || "pending").toUpperCase()}</span></td>
              <td className="px-3 py-2 text-xs">{p.status}</td>
              <td className="px-3 py-2">
                {p.approval_status === "approved" && p.status !== "executed" && (
                  <button onClick={() => release(p.id)} className="text-[10px] font-mono px-2 py-1 bg-[#FF3E00] text-white" data-testid={`pay-release-${p.id}`}>RELEASE</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mt-6 text-xs text-zinc-500">
        Tip · Approvals are handled in the <b>Approvals</b> module. After both reviewers approve, come back here and hit <b>RELEASE</b> to mark the payment executed (or connect an H2H bank channel in Phase 2).
      </div>
    </div>
  );
}
